/**
 * The multi-connection manager (PLAN §10.2): one primary backend for the data
 * path, and status-only probes of every watched endpoint (`GET /health`,
 * polled with backoff), exposed as one observable state for the panel and
 * `useSyncExternalStore`.
 *
 * Health checks run HERE, in the page, not in the service worker: Chrome stops
 * an idle service worker after ~30 s, so a polling loop there misses beats (and
 * the page needs the results anyway). docs/acp/status.md records the choice.
 *
 * The decisions (down after two failures, back to a preferred endpoint after
 * two successes, backoff, preference order) are the pure functions of
 * `state.ts`; this file only schedules probes and applies their results.
 * Clock, timers and the probe are injected.
 */
import type { HealthProbe } from '@acp/api-client/health.js';
import type { NodeRedundancyHealthWire } from '@acp/api-contract/health.js';
import type { StatusResponseWire } from '@acp/api-contract/statusRoutes.js';
import type { Endpoint } from './endpoints.js';
import { holdsLocalProbes, type LocalNetworkPort } from './localNetwork.js';
import {
  applyProbe,
  applyRequestFailure,
  choosePrimary,
  type ConnectivityState,
  type EndpointStatus,
  initialStatus,
  isDormant,
  probeDelayMs,
  type PairSummary,
  preferenceOrder,
  type ProbeOutcome,
  type SocketState,
} from './state.js';

/** How often the cloud's view (`GET /status`) is re-read while the cloud is up. */
export const CLOUD_VIEW_INTERVAL_MS = 30_000;

export type ConnectivityDeps = {
  readonly endpoints: readonly Endpoint[];
  readonly pageKind: 'cloud' | 'node';
  /** Probe one endpoint's `/health` (never throws: `probeHealth`). */
  readonly probe: (endpoint: Endpoint) => Promise<HealthProbe>;
  /** Read the cloud's view of the tenant's sites; null when it cannot (signed out, refused). Null: never read. */
  readonly readCloudView: ((cloud: Endpoint) => Promise<StatusResponseWire | null>) | null;
  /** The endpoints a cloud view names that are not watched yet (its nodes' HTTPS names, `statusEndpoints`): watched from then on. */
  readonly learnEndpoints: (view: StatusResponseWire, known: ReadonlySet<string>) => readonly Endpoint[];
  readonly after: (ms: number, fn: () => void) => () => void;
  readonly nowMs: () => number;
  /** Chrome's Local Network Access permission, read and followed (`localNetwork.ts`). */
  readonly localNetwork: LocalNetworkPort;
};

export type ConnectivityManager = {
  readonly getState: () => ConnectivityState;
  readonly subscribe: (listener: () => void) => () => void;
  /** The primary backend's endpoint. */
  readonly primary: () => Endpoint;
  /** Start probing (every endpoint at once, then on its own schedule). Idempotent. */
  readonly start: () => void;
  readonly stop: () => void;
  /** The operator's choice of primary ("Switch to local"); null returns to the default order. */
  readonly choose: (endpointId: string | null) => void;
  /** The primary's WebSocket state, from whoever holds the socket. */
  readonly reportSocket: (state: SocketState) => void;
  /** A data request to this endpoint failed at the network level: probe it now. */
  readonly reportUnreachable: (endpointId: string) => void;
  /**
   * "Connect to local servers": probe every studio server now, held or not — from the operator's click, so Chrome
   * shows its Local Network Access prompt (`localNetwork.ts`) — and read the permission again.
   */
  readonly connectLocal: () => void;
  /**
   * Resolves with the primary once it is up — the same endpoint recovered or
   * another one failed over to — or null after `timeoutMs`.
   */
  readonly whenPrimaryUp: (timeoutMs: number) => Promise<Endpoint | null>;
};

/** A node's pair as its `/health` says it, counted per role (PLAN §8.6: "which node is active per production"). */
export function pairSummaryOf(redundancy: NodeRedundancyHealthWire | undefined): PairSummary | null {
  if (redundancy === undefined) return null;
  const count = (role: 'active' | 'standby'): number => redundancy.productions.filter((p) => p.role === role).length;
  return { nodeRole: redundancy.nodeRole, peerLink: redundancy.peer.link, active: count('active'), standby: count('standby'), alarms: redundancy.productions.filter((p) => p.alarm !== null).length };
}

/** What one probe says about an endpoint (exported for its test). */
export function outcomeOf(probe: HealthProbe): ProbeOutcome {
  if (!probe.ok) return { ok: false, latencyMs: probe.latencyMs, error: `${probe.failure}: ${probe.message}`, failure: probe.failure };
  const h = probe.health;
  const node = h.service === 'state-service' ? { nodeId: h.nodeId, siteId: h.siteId, cloudLink: h.cloudLink, outboxDepth: h.outboxDepth, witness: h.redundancy?.witness ?? null, pair: pairSummaryOf(h.redundancy) } : null;
  return { ok: true, latencyMs: probe.latencyMs, version: h.version, stage: h.stage, node };
}

export function createConnectivityManager(deps: ConnectivityDeps): ConnectivityManager {
  const first = deps.endpoints.find((e) => e.servesPage) ?? deps.endpoints[0];
  if (first === undefined) throw new Error('createConnectivityManager: no endpoints to watch (the runtime config names at least its backend)');
  let state: ConnectivityState = {
    endpoints: deps.endpoints.map(initialStatus),
    pageKind: deps.pageKind,
    primaryId: first.id,
    chosenId: null,
    socket: 'unknown',
    cloudView: null,
    // Until read: a node's page needs nothing; a cloud page holds its local probes until it knows (`localRead`).
    localNetwork: deps.pageKind === 'node' ? 'not-needed' : 'unsupported',
  };
  let localRead = deps.pageKind === 'node';
  let stopWatching: (() => void) | null = null;
  const listeners = new Set<() => void>();
  const timers = new Map<string, () => void>();
  const inFlight = new Set<string>();
  /** Bumped by every failure report: a probe sent before the report cannot clear it. */
  const generation = new Map<string, number>();
  let running = false;
  let cloudViewAtMs: number | null = null;

  const publish = (next: ConnectivityState): void => {
    const primaryId = choosePrimary(next);
    // A new primary's socket is not the old one's: unknown until its holder reports.
    state = primaryId === next.primaryId ? next : { ...next, primaryId, socket: 'unknown' };
    for (const listener of listeners) listener();
  };
  const update = (id: string, change: (s: EndpointStatus) => EndpointStatus): void => {
    publish({ ...state, endpoints: state.endpoints.map((s) => (s.endpoint.id === id ? change(s) : s)) });
  };
  const statusOf = (id: string): EndpointStatus | undefined => state.endpoints.find((s) => s.endpoint.id === id);
  const endpointOf = (id: string): Endpoint => statusOf(id)?.endpoint ?? first;

  const refreshCloudView = async (cloud: Endpoint): Promise<void> => {
    const read = deps.readCloudView;
    if (read === null) return;
    const now = deps.nowMs();
    if (cloudViewAtMs !== null && now - cloudViewAtMs < CLOUD_VIEW_INTERVAL_MS) return;
    cloudViewAtMs = now;
    const view = await read(cloud).catch(() => null);
    // A failed read keeps the last good view: "the cloud's last-known status" is what the panel promises.
    if (!running || view === null) return;
    const learned = deps.learnEndpoints(view, new Set(state.endpoints.map((s) => s.endpoint.id)));
    publish({ ...state, cloudView: view, endpoints: [...state.endpoints, ...learned.map(initialStatus)] });
    for (const endpoint of learned) void probeNow(endpoint.id, false);
  };

  const schedule = (id: string, ms: number): void => {
    timers.get(id)?.();
    timers.set(
      id,
      deps.after(ms, () => {
        timers.delete(id);
        void probeNow(id, false);
      }),
    );
  };

  /** A studio server seen from a public page: what Local Network Access governs. */
  const isLocal = (endpoint: Endpoint): boolean => endpoint.kind === 'node' && state.pageKind === 'cloud';
  /** Held: a local server while the permission is unread, `prompt` or `denied` — no request, no timer, until it changes. */
  const held = (endpoint: Endpoint): boolean => isLocal(endpoint) && (!localRead || holdsLocalProbes(state.localNetwork));
  /** A probe not to send: held, unless the operator asked for it (a click). */
  const skipped = (endpoint: Endpoint, force: boolean): boolean => !force && held(endpoint);

  async function probeNow(id: string, force: boolean): Promise<void> {
    if (!running || inFlight.has(id)) return;
    const endpoint = endpointOf(id);
    if (skipped(endpoint, force)) return;
    const sentAt = generation.get(id) ?? 0;
    inFlight.add(id);
    let result: HealthProbe;
    try {
      result = await deps.probe(endpoint);
    } finally {
      inFlight.delete(id);
    }
    if (!running) return;
    // A data request failed while this probe was out: its answer may predate the failure. Ask again.
    if ((generation.get(id) ?? 0) !== sentAt) {
      void probeNow(id, force);
      return;
    }
    applyResult(endpoint, result);
    if (endpoint.kind === 'cloud' && result.ok) await refreshCloudView(endpoint);
  }

  /** One probe's answer: the endpoint's status, its next probe, and — the primary down — the dormant ones woken. */
  function applyResult(endpoint: Endpoint, result: HealthProbe): void {
    const id = endpoint.id;
    const nowMs = deps.nowMs();
    const wasPrimary = id === state.primaryId;
    const onDataPath = endpoint.servesPage || wasPrimary || id === state.chosenId;
    const probed = applyProbe(statusOf(id) ?? initialStatus(endpoint), outcomeOf(result), nowMs);
    const delay = probeDelayMs(probed, onDataPath, nowMs);
    update(id, () => ({ ...probed, nextProbeAtMs: nowMs + delay }));
    schedule(id, delay);
    // The primary is down: a dormant endpoint may be back by now — ask them all at once, so failover need not wait minutes.
    if (wasPrimary && probed.reach === 'down') wakeDormant(nowMs);
  }

  /** The permission is known, or changed: record it, and probe the local servers it no longer holds back. */
  function applyLocalNetwork(access: ConnectivityState['localNetwork']): void {
    localRead = true;
    if (access !== state.localNetwork) publish({ ...state, localNetwork: access });
    if (!running) return;
    for (const s of state.endpoints) if (isLocal(s.endpoint) && !held(s.endpoint)) void probeNow(s.endpoint.id, false);
  }

  function wakeDormant(nowMs: number): void {
    for (const s of state.endpoints) if (isDormant(s, nowMs)) void probeNow(s.endpoint.id, false);
  }

  const waiters = new Set<() => void>();
  listeners.add(() => {
    // A waiter that resolves deletes itself; deleting the current entry of a Set mid-iteration is safe.
    for (const w of waiters) w();
  });

  return {
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    primary: () => endpointOf(state.primaryId),
    start: () => {
      if (running) return;
      running = true;
      for (const s of state.endpoints) void probeNow(s.endpoint.id, false);
      if (!localRead) {
        void deps.localNetwork.read().then(applyLocalNetwork, () => applyLocalNetwork('unsupported'));
        stopWatching = deps.localNetwork.watch(applyLocalNetwork);
      }
    },
    stop: () => {
      running = false;
      stopWatching?.();
      stopWatching = null;
      for (const cancel of timers.values()) cancel();
      timers.clear();
    },
    choose: (endpointId) => {
      if (endpointId !== null && statusOf(endpointId)?.endpoint.canBePrimary !== true) return;
      // The operator's word takes effect at once when its endpoint is up (the most preferred one that is, for
      // "use the default"); otherwise the usual rules move there once it is stably up.
      const next = { ...state, chosenId: endpointId };
      const target = preferenceOrder(next).find((id) => statusOf(id)?.reach === 'up');
      const primaryId = target ?? state.primaryId;
      publish({ ...next, primaryId, socket: primaryId === state.primaryId ? state.socket : 'unknown' });
      // Not up (or dormant, its next probe minutes away): ask it now, so the choice takes effect once it answers.
      if (endpointId !== null && target !== endpointId) void probeNow(endpointId, true);
    },
    connectLocal: () => {
      for (const s of state.endpoints) if (isLocal(s.endpoint)) void probeNow(s.endpoint.id, true);
      // Chrome answers its prompt after the click; the watch hears that too, but a browser without change events does not.
      void deps.localNetwork.read().then(applyLocalNetwork, () => undefined);
    },
    reportSocket: (socket) => {
      if (socket !== state.socket) publish({ ...state, socket });
    },
    reportUnreachable: (endpointId) => {
      if (statusOf(endpointId) === undefined) return;
      generation.set(endpointId, (generation.get(endpointId) ?? 0) + 1);
      update(endpointId, applyRequestFailure);
      void probeNow(endpointId, false);
    },
    whenPrimaryUp: (timeoutMs) =>
      new Promise<Endpoint | null>((resolve) => {
        const check = (): boolean => {
          if (statusOf(state.primaryId)?.reach !== 'up') return false;
          done(endpointOf(state.primaryId));
          return true;
        };
        let cancel: () => void = () => undefined;
        const done = (value: Endpoint | null): void => {
          waiters.delete(waiter);
          cancel();
          resolve(value);
        };
        const waiter = (): void => {
          check();
        };
        if (check()) return;
        waiters.add(waiter);
        cancel = deps.after(timeoutMs, () => done(null));
      }),
  };
}
